import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Provider, defaultTheme, darkTheme, ProgressCircle, ActionButton, Divider, Flex } from '@adobe/react-spectrum';

import {
  updatecurrentContactId,
  setCISloader,
  updateConversationStatus,
  updatejcAuthData,
  showSearchCustomerTable,
  updatePresistChatInfo,
  customerOffer,
  cisAllowAgent,
  setClosedContactSentiments,
} from '../Store/actions/customerInfoActions';
import { SEARCH_CUSTOMER, USER_PERSISTED } from '../Store/constants/customerInfoConstants';
import { updateSearchCustomerDetails } from '../Store/actions/customerInfoActions';
import useAttributeData from '../hooks/useAttributeData';
import useCustomerOfferFetch from '../hooks/useCustomerOfferFetch';
import useUserPersisted from '../hooks/useUserPersisted';
import useOrderQuote from '../hooks/useOrderQuote';
import useCartDetail from '../hooks/useCartDetail';

import Accordion from './Accordion';
import Refreshs from '../Theme/Icons/Refreshs';
import LinkedCustomer from '../Theme/Icons/linkedCustomer';
import styles from '../Theme/Styles/styles.module.css';
import themes from '../Theme/Themes';
import localization from '../Language/localization';
import useGetClosedContactPathSentimentHook from '../hooks/useGetClosedContactPathSentimentHook';
import useFetchAgentPathSentimentsHook from '../hooks/useFetchAgentPathSentimentsHook';


interface Props {
  contextData: any;
  callBackEvent: (eventType: string, eventData: any) => void;
  darkMode: boolean;
  CIP: boolean;
  language: string;
  conversationSummary: any;
  conversationSentiments: {
    [contactId: string]: string;
  };
  conversationSentimentsLoading: {
    [contactId: string]: boolean;
  };
  sentimentEnabled: boolean;
  trendLineData: any;
  feedback: any;
  agentPath: Array<{ DisplayName: string; ContactId: string; ParticipantId: string }> | string;
  enableSummary: boolean;
  urls: {
    HENDRIX_DOMAIN: string;
    CM_DOMAIN: string;
    CIP_DOMAIN: string;
    CCP_API_END_POINT: string;
    RTS_PROCESSOR_ENDPOINT: string;
  };
}

const CustomerInfoPanel = (prop: Props) => {
  const {
    CIP,
    urls,
    contextData,
    callBackEvent,
    conversationSummary,
    conversationSentiments,
    conversationSentimentsLoading,
    sentimentEnabled,
    trendLineData,
    enableSummary,
    agentPath,
    feedback,
  } = prop;
  const [reload, setReload] = useState(1);

  const {
    ocPlatformData: {
      chatInfo: { contactId = '', status = '', currentContactId = '' } = {},
      allowedAgent = false,
      callInfo: { assignedAgent = '', origin: callOrigin = '' } = {},
      agentInfo: { agentSiteLocation = '' } = {},
      transferInProgressId = '',
      chatTranscript = [],
    } = {},
    ocAppData: { closedConversation = '', closedPhoneCall = '', originForCurrentConversation = '', mockOfferIdFlag = false },
    jcMidConvData: {
      HostContextInfo = '',
    },
    jcAuthData: { customerId = '', linkedConversationId = '' } = {}
  } = contextData;
  let customerSearchInput = ''
  if (!(closedConversation && closedPhoneCall)) {
    customerSearchInput = customerId
  }
  let customConversationId = contactId;
  
  //API hooks
  useAttributeData(contextData, prop.darkMode, CIP, prop.language);
  const { CIP_data : newData } = useSelector((state: any) => state.customerInfo);
  //chat to phone and phone leg ( where there is no chat transcript)
  if( originForCurrentConversation === 'chat-to-phone' ) {
    const convObject = newData?.filter((item => item.Title === 'Conversation Info'))[0];
    customConversationId = convObject?.["Chat ID"]?.[0] || contactId;
  }
  useUserPersisted(urls.CIP_DOMAIN, customConversationId, customerSearchInput, callBackEvent, originForCurrentConversation);
  useCustomerOfferFetch(contextData, CIP, urls.CIP_DOMAIN, reload, agentSiteLocation);
  useOrderQuote(CIP, urls.CIP_DOMAIN, reload);
  useCartDetail(urls.CIP_DOMAIN, HostContextInfo, mockOfferIdFlag, reload, contactId);
  const { getSentiments } = useGetClosedContactPathSentimentHook(urls.RTS_PROCESSOR_ENDPOINT);
  const { fetchOrGenerateSentiments } = useFetchAgentPathSentimentsHook(urls.RTS_PROCESSOR_ENDPOINT);
  const isConversationActive: boolean = useSelector((state: any) => state.customerInfo.isConversationActive);
  const { CIP_data, SCIP_data, userPersisted, searchedUser, loading, language, presistChatInfo } = useSelector((state: any) => state.customerInfo);
  let contextualData: any = CIP ? CIP_data : SCIP_data;
  const dispatch = useDispatch();

  const linkedUserClicked = () => {
    const persis = userPersisted?.linkedCustomer?.ldap
    const current = searchedUser?.linkedCustomer?.ldap
    console.log('linkedUserClicked', persis, current)
    if (persis !== current) {
      dispatch(updateSearchCustomerDetails(userPersisted));
    }
  }
  const { darkMode, darkModeBG, darkModeBorder, darkModea, darkModeTitle, darkModeaAdobeRegular } = themes(useSelector((state: any) => state.customerInfo.darkMode));
  const refresh = () => {
    console.log('CIP/SCIP refresh');
    dispatch(setCISloader(true));
    presistChatInfo[contactId] = {
      phone: '',
      email: userPersisted?.linkedCustomer?.email ? userPersisted.linkedCustomer.email : '',
      searchCustomer: '',
    };
    dispatch(showSearchCustomerTable(false));
    dispatch(updatePresistChatInfo(presistChatInfo));
    setReload(key => key + 1); // force reload
    callBackEvent('CIP_REPHRESH', {});
    // refresh the sentiments
    if (isConversationActive && contactId) {
      fetchOrGenerateSentiments(Array.isArray(agentPath) ? agentPath.map(agent => agent.DisplayName === 'BOT' ? `${agent.ContactId}-BOT` : agent.ContactId) : [], contactId)
        .then(sentiments => {
          callBackEvent('SET_SENTIMENTS', sentiments ?? {})
        })
        .catch(err => console.log('Error in CIP Home fetching sentiments:  ', err?.message ?? 'Internal Error'));
    } else if (!isConversationActive && contactId) {
      getSentiments(contactId)
        .then((res) => dispatch(setClosedContactSentiments(res?.listOfAllContactIdsSentiment ?? [])))
        .catch(err => console.log('Error in the ConversationSummary CIP fetching sentiments for a closed contact: ', err?.message ?? 'Internal Error'))
    }
  };
  //reset and clear the state
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      dispatch(updatecurrentContactId(contactId));
      dispatch(updatejcAuthData(contextData?.jcAuthData));
      dispatch(customerOffer(CIP, []));
      const isSearchCustomerPersist = presistChatInfo[contactId]?.searchCustomer;
      console.log('SEARCH_CUSTOMER Persist', isSearchCustomerPersist, presistChatInfo);
      if ((isSearchCustomerPersist || contextData.jcAuthData.isMultipleAccountLinked === 'true') && !(closedConversation && closedPhoneCall)) {
        if (isSearchCustomerPersist) dispatch({ type: SEARCH_CUSTOMER, payload: presistChatInfo[contactId].searchCustomer });
        dispatch(showSearchCustomerTable(true));
      } else dispatch(showSearchCustomerTable(false));
    }
    return () => {
      dispatch({
        type: USER_PERSISTED,
        payload: [],
      });
      isMounted = false;
    };
  }, [contactId]);


  // isConversation Active
  useEffect(() => {
    const isCall = !(originForCurrentConversation === 'inbound-chat' || (originForCurrentConversation === 'chat-to-phone' && !closedConversation && closedPhoneCall));
    const isConversationActive = isCall ? assignedAgent : true;
    const isActive = !(closedConversation && closedPhoneCall) && isConversationActive;
    console.log('SCIP updateConversationStatus action', status, isConversationActive, isActive, contactId, CIP);
    dispatch(updateConversationStatus(CIP, isActive));
  }, [status, assignedAgent]);

  useEffect(() => {
    dispatch(cisAllowAgent(allowedAgent));
  }, [allowedAgent]);

  if (enableSummary) {
    contextualData = [(contextualData as Array<any>)[0], { Title: 'Current Conversation Summary' }, ...(contextualData as Array<any>).slice(1)];
  }

  return (
    <Provider theme={darkMode ? darkTheme : defaultTheme} colorScheme="light" UNSAFE_style={{ height: '100%', borderRadius: '5px' }}>
      <div className={styles['customer-info-panel'] + darkModeBG}>
        <div className={styles['cip-widget-header'] + darkModeBG + darkModeBorder + darkModeTitle}>
          <div className={styles['widget-icon']}>{CIP ? 'EI' : 'SC'}</div>
          <span>{CIP ? localization[language]['cip-title-header'] : localization[language]['scip-title-header']}</span>
          <div className={styles['cip-refresh-button'] + darkModeTitle}>
            <ActionButton UNSAFE_className={styles['cip-refresh-action-button'] + darkModeTitle} isQuiet onPress={refresh}>
              <Refreshs />
            </ActionButton>
          </div>
        </div>
        <Divider size='S' />
        <Flex height='40px' justifyContent='left' alignItems='center' >
          <LinkedCustomer isDarkMode={darkMode} />
          <div style={{ "paddingRight": "10px" }}> {userPersisted?.linkedCustomer?.ldap ? "Linked Employee :" : "No Employee is linked"}</div>
          {userPersisted?.linkedCustomer?.ldap && <button
            onClick={() => linkedUserClicked()}
            className={styles['accordion-content-fields-values-phone'] + darkModeBG + darkModea}>
            {userPersisted?.linkedCustomer?.name?.length > 20
              ? userPersisted.linkedCustomer.name.slice(0, 20) + '...'
              : userPersisted?.linkedCustomer?.name}
          </button>}

        </Flex>
        <Divider size='S' />
        <div className={styles['cip-widget-content']}>

          {loading ? (
            <div className={styles['cip-loader']}>
              <ProgressCircle UNSAFE_className={styles['cip-module-fetch-loader']} aria-label="Loading…" isIndeterminate />
              <p className={`${styles['cip-loader-text']} ${styles['adobe-clean-regular']}` + darkModeaAdobeRegular}>
                {localization[language]['loader-fetching-customer-profile']}
              </p>
            </div>
          ) : (
            // Loop and creating diffrent Accordion session in CIP/SICP
            contextualData.map((item: any) => (
              <Accordion
                key={item?.Title}
                title={item?.Title}
                cipValues={item}
                isCIP={CIP}
                urls={urls}
                reload={reload}
                chatStatus={status}
                callOrigin={callOrigin}
                transferInProgressId={transferInProgressId}
                conversationSummary={conversationSummary}
                conversationSentiments={conversationSentiments}
                conversationSentimentsLoading={conversationSentimentsLoading}
                sentimentEnabled={sentimentEnabled}
                trendLineData={trendLineData}
                agentPath={agentPath}
                setReload={setReload}
                currentConvId={currentContactId}
                initialContactId={customConversationId}
                feedback={feedback}
                callBackEvent={callBackEvent}
              />
            ))
          )}
        </div>
      </div>
    </Provider>
  );
};

export default CustomerInfoPanel;
